import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";

import type { ReactNode } from "react";
import {
  Briefcase,
  Building2,
  Contact,
  CreditCard,
  GitBranch,
  Handshake,
  HardDrive,
  Inbox,
  LayoutDashboard,
  MapPin,
  Newspaper,
  Receipt,
  Settings,
  Sprout,
  Telescope,
  Users,
} from "lucide-react";

import { inboundStatsOptions } from "../api/hooks";
import { useAuth } from "../auth/AuthProvider";

import styles from "./Sidebar.module.css";

// How many submissions are waiting on a reply, shown against Inbound so a new
// one is visible from anywhere in Lodge. Counts applications and enquiries
// together, because a badge that covered only one of them could read zero
// while somebody sat waiting. Hidden at zero, while loading and on error: the
// badge is a nudge, not a health indicator.
function WaitingSubmissionsBadge() {
  const { data } = useQuery({ ...inboundStatsOptions, staleTime: 60_000 });

  if (!data?.waitingCount) return null;

  // The label gives the bare digit a unit, so the link reads as
  // "Inbound, 4 waiting" rather than a naked number.
  return (
    <span
      className={styles.badge}
      aria-label={`${data.waitingCount} waiting`}
      title={`${data.waitingCount} waiting`}
    >
      {data.waitingCount}
    </span>
  );
}

interface NavItem {
  label: string;
  icon: ReactNode;
  to: string;
  badge?: ReactNode;
}

const navSections: { label: string; items: NavItem[] }[] = [
  {
    label: "Overview",
    items: [{ label: "Dashboard", icon: <LayoutDashboard size={16} />, to: "/" }],
  },
  // What the sector is, rather than who we sell to. Organisations is every body
  // we know of whether or not they are a customer, so it sits here rather than
  // under Manage, which is the accounts we hold - and the news is what is being
  // said about those same bodies. The bodies, then the people at them, then
  // what is said about them.
  //
  // The design puts the news first. Kept in this order because the reason
  // above is the one written down and the news is the least of the three; say
  // so if the design's order is meant literally.
  {
    label: "Sector",
    items: [
      { label: "Organisations", icon: <Building2 size={16} />, to: "/organisations" },
      { label: "People", icon: <Contact size={16} />, to: "/people" },
      { label: "Sector news", icon: <Newspaper size={16} />, to: "/sector-news" },
    ],
  },
  {
    label: "Manage",
    items: [
      { label: "Customers", icon: <Briefcase size={16} />, to: "/customers" },
      { label: "Users", icon: <Users size={16} />, to: "/users" },
      { label: "Sites", icon: <MapPin size={16} />, to: "/sites" },
    ],
  },
  {
    label: "Billing",
    items: [
      { label: "Subscriptions", icon: <CreditCard size={16} />, to: "/subscriptions" },
      { label: "Invoices", icon: <Receipt size={16} />, to: "/invoices" },
    ],
  },
  {
    label: "Growth",
    items: [
      {
        label: "Inbound",
        icon: <Inbox size={16} />,
        to: "/inbound",
        badge: <WaitingSubmissionsBadge />,
      },
      // Before the pipeline, because that is the order the work happens in: a
      // find is judged, and what survives is contacted.
      {
        label: "Lead research",
        icon: <Telescope size={16} />,
        to: "/lead-research",
      },
      // Between Inbound and Referrals: an enquiry arrives, becomes someone we
      // are working, and a referral is what a customer sends afterwards.
      { label: "Pipeline", icon: <GitBranch size={16} />, to: "/pipeline" },
      { label: "Referrals", icon: <Handshake size={16} />, to: "/referrals" },
      { label: "Storage", icon: <HardDrive size={16} />, to: "/storage" },
    ],
  },
  {
    label: "System",
    items: [{ label: "Settings", icon: <Settings size={16} />, to: "/settings" }],
  },
];

export function Sidebar() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { user } = useAuth();

  const avatarLetter = user?.name ? user.name.charAt(0).toUpperCase() : "?";

  return (
    <nav className={styles.sidebar}>
      <Link to="/" className={styles.logo}>
        <div className={styles.logoIcon}>
          <Sprout size={14} />
        </div>
        Lodge
      </Link>

      {navSections.map((section) => (
        <div key={section.label}>
          <div className={styles.label}>{section.label}</div>
          {section.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`${styles.link} ${currentPath === item.to ? styles.active : ""}`}
            >
              <span className={styles.linkIcon}>{item.icon}</span>
              {item.label}
              {item.badge}
            </Link>
          ))}
        </div>
      ))}

      <div className={styles.spacer} />
      <div className={styles.user}>
        <div className={styles.avatar}>{avatarLetter}</div>
        <div className={styles.userInfo}>
          <div className={styles.userName}>{user?.name ?? "-"}</div>
          <div className={styles.userRole}>{user?.email ?? ""}</div>
        </div>
      </div>
    </nav>
  );
}
